import type { ExternalTableSchema } from '@packages/table-builder';
import { IMPORT_EXPORT_SEMANTIC_IDS } from '@packages/table-builder/utils/importExportSemanticIds';

export const exerciseTable: ExternalTableSchema = {
  metadata: {
    title: 'Exercise',
    entity: 'Exercise',
  },

  dataSource: {
    endpoint: '/api/award/api/app/option-exercise/option-exercised-list',
    method: 'GET',
    rowIdField: 'id',
    responseMapping: {
      items: 'items',
      totalCount: 'totalCount',
    },
    requestParams: {
      Filter: 'table.searchText',
      MaxResultCount: 'table.pageSize',
      SkipCount: 'table.pageOffset',
      Sorting: 'table.sorting',
    },
    filters: [
      {
        name: 'createdFromDate',
        placeholder: 'Exercise Date From',
        type: 'date',
      },
      {
        name: 'createdToDate',
        placeholder: 'Exercise Date To',
        type: 'date',
      },
      {
        name: 'optionGrantName',
        placeholder: 'Grant Name',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/award/api/app/option-award/grant-details',
          labelField: 'grantName',
          valueField: 'grantId',
        },
      },
      {
        name: 'settlementTypeIds',
        placeholder: 'Settlement Type',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/award/api/app/option-exercise/settlement-types',
          labelField: 'displayName',
          valueField: 'id',
        },
      },
      {
        name: 'recordTypes',
        placeholder: 'Record Type',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/award/api/app/status-dropdown-app-services/exercise-record-types',
          labelField: 'displayName',
          valueField: 'id',
        },
      },
      {
        name: 'showProcessable',
        placeholder: 'Show Processable',
        type: 'select',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
    ],
  },

  columns: [
    { field: 'awardNumber', header: '#Awards', type: 'text', sortable: true, width: 100 },
    {
      field: 'participantName',
      header: 'Participant Details',
      type: 'couple',
      sortable: true,
      width: 200,
      couple: { titleField: 'participantName', subtitleField: 'employeeId' },
    },
    { field: 'grantName', header: 'Grant Name', type: 'text', sortable: true, width: 200 },
    { field: 'exercisedDate', header: 'Exercise Date', type: 'date', sortable: true, width: 200 },
    { field: 'exercisedUnit', header: 'Exercise Unit', type: 'number', sortable: true, width: 200, align: 'center' },
    {
      field: 'exerciseStatus',
      header: 'Exercise Status',
      type: 'badge',
      sortable: true,
      width: 250,
      align: 'center',
      badge: {
        statusMap: {
          Failed: 'error',
          'In Progress': 'info',
          Cancelled: 'error',
          Successful: 'success',
        },
        defaultVariant: 'default',
      },
    },
    { field: 'settlementTypeDisplayName', header: 'Settlement Type', type: 'text', sortable: true, width: 200 },
    {
      field: 'fmv',
      header: 'FMV $',
      type: 'currency',
      sortable: true,
      width: 180,
      align: 'center',
      format: { decimals: 4 },
    },
    {
      field: 'recordType',
      header: 'Record Type',
      type: 'badge',
      sortable: true,
      width: 300,
      align: 'center',
      badge: {
        statusMap: {
          'Option Intent': 'info',
          'Options Exercise': 'success',
          OptionsExercise: 'success',
          'Early Exercise': 'success',
          EarlyExercise: 'success',
          'Exercise Allocate': 'success',
          ExerciseAllocate: 'success',
        },
        defaultVariant: 'success',
      },
    },
    {
      field: 'isTaxCalculated',
      header: 'Tax Calculation Type',
      type: 'badge',
      sortable: true,
      width: 300,
      align: 'center',
      visibility: 'picker',
      conditionalValues: { true: 'CALCULATED', false: 'NOT CALCULATED' },
      badge: { statusMap: { true: 'success', false: 'error' }, defaultVariant: 'error' },
    },
  ],

  actions: {
    row: [
      { id: 'VIEW_DETAILS_EXERCISE', label: 'View Details' },
      { id: 'CALCULATE_TAX_EXERCISE', label: 'Calculate Tax' },
      { id: 'CANCEL_EXERCISE', label: 'Cancel', variant: 'danger' },
      { id: 'REVERSE_DELETE_EXERCISE', label: 'Reverse & Delete', variant: 'danger' },
    ],
    toolbar: {
      secondary: [
        {
          label: 'Download Template',
          icon: 'download',
          type: 'download',
          semanticId: IMPORT_EXPORT_SEMANTIC_IDS.downloadTemplate,
          children: [{ id: 'DOWNLOAD_TEMPLATE_EXERCISE', label: 'Download Template' }],
        },
        {
          label: 'Import Bulk XLS',
          icon: 'upload',
          type: 'upload',
          semanticId: IMPORT_EXPORT_SEMANTIC_IDS.importTemplate,
          children: [{ id: 'IMPORT_BULK_EXERCISE', label: 'Import Bulk XLS' }],
        },
        { id: 'APPLY_FMV_EXERCISE', label: 'Apply FMV' },
        { id: 'CALCULATE_TAX_BULK_EXERCISE', label: 'Calculate Tax' },
        { id: 'VIEW_CLOSING_PRICE_EXERCISE', label: 'View Closing Price' },
        { id: 'READY_TO_PROCESS_EXERCISE', label: 'Ready to Process' },
      ],
    },
  },

  features: {
    search: { enabled: true },
    pagination: { mode: 'infiniteScroll', defaultSize: 50 },
    selection: {
      keyField: 'id',
      selectable: { field: 'isExerciseProcessable', operator: 'EQUAL', value: true },
    },
    columnPreferences: { persistenceKey: 'Exercise', resizable: true, reorderable: true },
  },
};
